import { Button } from "@/app/common/common-components/ui/Button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/app/common/common-components/ui/Table";
import { MessageTemplate } from "@/app/models/MessageTemplateModel";
import { Edit, Trash } from "lucide-react";
import React, { useState } from "react";
import EditTemplateDialog from "./EditTemplateDialog";
import DeleteTemplateConfirmationDialog from "./DeleteTemplateConfirmationDialog";
import apiAgent from "@/app/api/apiAgent";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import NewLoader from "@/app/layout/NewLoader";

interface MessageTemplatesTabProps {
  officeId: string;
  userId: string;
}

const MessageTemplatesTab: React.FC<MessageTemplatesTabProps> = ({
  officeId,
  userId,
}) => {
  const queryClient = useQueryClient();

  const { data: messageTemplates, status } = useQuery({
    queryKey: ["messageTemplates", officeId, userId],
    queryFn: () => apiAgent.MessageTemplate.getMessageTemplates(userId),
  });

  const { data: pipelines } = useQuery({
    queryKey: ["pipelines", officeId, userId],
    queryFn: () => apiAgent.Pipeline.getPipelines("deal", officeId),
  });

  const { mutate: deleteTemplate, isPending: isDeleting } = useMutation({
    mutationFn: (templateId: string) =>
      apiAgent.MessageTemplate.deleteMessageTemplate({ templateId, userId }),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["messageTemplates", officeId, userId],
      });
      setTemplateToDelete(undefined);
    },
  });

  const { mutate: saveTemplate, isPending: isSaving } = useMutation({
    mutationFn: (template: MessageTemplate) =>
      apiAgent.MessageTemplate.updateMessageTemplate(template),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["messageTemplates", officeId, userId],
      });
      setEditingTemplate(undefined);
    },
  });

  const { mutate: createTemplate, isPending: isCreating } = useMutation({
    mutationFn: (template: MessageTemplate) =>
      apiAgent.MessageTemplate.createMessageTemplate(template),
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["messageTemplates", officeId, userId],
      });
      setEditingTemplate(undefined);
    },
  });

  const handleTemplateSave = (template: MessageTemplate) => {
    if (template.templateId && template.templateId !== "") {
      saveTemplate(template);
    } else {
      createTemplate(template);
    }
  };

  const handleDeleteConfirm = () => {
    if (templateToDelete) {
      deleteTemplate(templateToDelete.templateId);
    }
  };

  const [editingTemplate, setEditingTemplate] = useState<
    MessageTemplate | undefined
  >();
  const [templateToDelete, setTemplateToDelete] = useState<
    MessageTemplate | undefined
  >();

  const handleNewTemplateClick = () => {
    setEditingTemplate({
      templateId: "",
      templateName: "",
      templateType: "Email",
      content: "",
      userId,
      officeId,
      subject: "",
      pipelineId: "",
      stageId: "",
      isSharedWithOffice: false,
    });
  };

  if (status === "pending") {
    return <NewLoader text="Loading message templates..." iconOnly />;
  }

  return (
    <>
      <div className="tw-flex tw-flex-col">
        <div className="tw-flex tw-justify-end">
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleNewTemplateClick();
            }}
          >
            New Template
          </Button>
        </div>
        {/* Templates table */}
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Pipeline</TableHead>
              <TableHead>Milestone</TableHead>
              <TableHead>Shared</TableHead>
              <TableHead className="tw-text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {messageTemplates?.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="tw-text-center">
                  No templates found
                </TableCell>
              </TableRow>
            )}
            {messageTemplates?.map((template) => (
              <TableRow key={template.templateId}>
                <TableCell>{template.templateName}</TableCell>
                <TableCell>{template.templateType}</TableCell>
                <TableCell>
                  {pipelines?.find((p) => p.pipeline_id === template.pipelineId)
                    ?.pipeline_name || "-"}
                </TableCell>
                <TableCell>
                  {pipelines
                    ?.find((p) => p.pipeline_id === template.pipelineId)
                    ?.milestone.find((m) => m.milestone_id === template.stageId)
                    ?.milestone_name || "-"}
                </TableCell>
                <TableCell>
                  {template.isSharedWithOffice ? "Yes" : "No"}
                </TableCell>
                <TableCell className="tw-flex tw-justify-end tw-gap-2 tw-text-right">
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={(e) => {
                      e.preventDefault();
                      setEditingTemplate(template);
                    }}
                  >
                    <Edit />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={(e) => {
                      e.preventDefault();
                      setTemplateToDelete(template);
                    }}
                  >
                    <Trash />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <EditTemplateDialog
        isOpen={editingTemplate !== undefined}
        onClose={() => setEditingTemplate(undefined)}
        template={editingTemplate}
        onSave={handleTemplateSave}
        userId={userId}
        officeId={officeId}
        isSaving={isSaving || isCreating}
      />

      <DeleteTemplateConfirmationDialog
        isOpen={templateToDelete !== undefined}
        onClose={() => setTemplateToDelete(undefined)}
        template={templateToDelete}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default MessageTemplatesTab;
